import { useEffect, useMemo, useState } from 'react'
import { api, type BlastRadius, type PendingRule } from '../api'
import { FilterHeader, Showing, SortHeader, sortRows, useSort } from '../components/Grid'
import { RowMenu } from '../components/RowMenu'
import { Retirements } from '../components/Retirements'
import { layerLabel, says, subjectText } from '../limits'
import { DeskCell, DeskHeader, useDesks } from '../desks'

/**
 * The approval queue — TECHNICAL_DESIGN.md §8.6, §5.6.
 *
 * Only `approve` writes `active`, for every channel, because *"a perfectly
 * extracted value from a fully authorised sender is still a proposal"*. This
 * screen is the second step, and it is built around the one thing it must not
 * let an approver do: sign off a scope rule without having seen what it reaches.
 *
 * **The blast radius is fetched, displayed, and echoed back.** `shown_portfolio_ids`
 * comes from the response the approver was shown, so a membership change between
 * the two requests is *refused* by the store (§5.6, defect 3) rather than
 * reconciled. Approving is disabled until the radius has loaded — not as a
 * courtesy, but because approving without it is the exact act the control exists
 * to prevent.
 *
 * **The queue is a table**, because an approver working through twenty proposals
 * is comparing them — the same three questions of each, in the same place. Stacked
 * cards made each proposal legible on its own and the twenty of them a scroll.
 *
 * **The fund leads it, and its own header sorts and filters it.** The queue
 * arrives in effective-date order, which is the right default and the wrong
 * order for the way it is worked: somebody has one mandate open and wants that
 * fund's rows together. A table sorted by a date nobody is reading scatters
 * them. The controls sit on the header rather than in a bar above the table,
 * because *what is this column showing* is a question asked while looking at the
 * column, and a bar is a second place to go and look.
 *
 * **The layer has its own column.** It used to be a grey word under the rule key,
 * where it read as decoration — and since nearly every proposal is `mandate`, the
 * one that says `house` or `regulatory` is precisely the row that should stop
 * somebody, and was the row that looked like all the others. As a column it is
 * comparable down the page and filterable.
 *
 * **Several can be decided at once**, and the controls do not relax to allow it.
 * Each rule still echoes the radius *it* was shown, a scope rule whose radius has
 * not arrived cannot be ticked, and refusals are per-rule and reported by name —
 * a batch that stopped at the first failure would leave the approver guessing
 * which of the twenty went through. What bulk removes is the twentieth identical
 * click, which was never the thing making anybody think. Refusals are grouped by
 * reason, each reason said once over the limits it refused, so thirteen of the
 * same refusal read as one sentence and a list rather than thirteen sentences.
 *
 * **A proposal the reader made is marked, and cannot be ticked or approved.** The
 * store refuses it anyway; saying so on the row, before anybody tries, is better
 * than a batch coming back refused.
 *
 * A 409 is surfaced verbatim. Those messages are written for the person who hit
 * them — *this rule reached 3 portfolios when the radius was shown and reaches 4
 * now* — and replacing them with "conflict" would discard the instruction.
 */

/** What a proposal attaches to, as one string. A scope-attached rule has no
 *  fund of its own — it has a group, and the radius under the cell says which
 *  funds that group currently holds. */
function appliesTo(rule: PendingRule): string {
  return rule.portfolio_code ?? rule.scope_code ?? 'Unattached'
}

const YOURS = 'You proposed this, so another approver puts it into force.'

type Outcome = {
  done: number
  action: 'approve' | 'reject'
  refused: { key: string; reason: string }[]
}

/** A reason as a sentence: capitalised and ending in a full stop. */
function sentence(reason: string): string {
  const trimmed = reason.trim()
  const capital = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  return /[.!?]$/.test(capital) ? capital : `${capital}.`
}

/** The refusals by reason, in the order each reason first came up. */
function byReason(refused: Outcome['refused']): [string, string[]][] {
  const groups = new Map<string, string[]>()
  for (const { key, reason } of refused) groups.set(reason, [...(groups.get(reason) ?? []), key])
  return [...groups.entries()]
}

export function Approvals({ canApprove, me }: { canApprove: boolean; me?: string }) {
  const [queue, setQueue] = useState<PendingRule[]>([])
  const [radius, setRadius] = useState<Record<string, BlastRadius>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  /** How far through a batch we are. A hundred and forty-six approvals is a
   *  hundred and forty-six round trips, and a screen that shows nothing for the
   *  duration is indistinguishable from one that has stopped working. */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [fund, setFund] = useState('')
  const [layer, setLayer] = useState('')
  const { sort, onSort } = useSort()
  /** The desk the queue is narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  /** A proposal's desk: its fund's, or its group's ("Whole firm" for the firm's own). */
  const deskOf = (r: PendingRule) => (r.portfolio_code ? desks.fund(r.portfolio_code) : desks.group(r.scope_code)) ?? ''

  async function load() {
    try {
      const pending = await api.pendingRules()
      setQueue(pending)
      const still = new Set(pending.map((p) => p.rule_id))
      setPicked((was) => new Set([...was].filter((id) => still.has(id))))
      // Pre-fetch the radius for every scope proposal, so what the approver is
      // looking at when they decide is on the screen rather than behind a click.
      const scoped = pending.filter((p) => p.scope_id)
      const radii = await Promise.all(
        scoped.map((p) => api.blastRadius(p.rule_id).then((r) => [p.rule_id, r] as const)),
      )
      setRadius(Object.fromEntries(radii))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  /** A scope rule whose radius has not arrived cannot be decided — the same
   *  gate the menu item has, moved to the checkbox so a batch cannot smuggle
   *  one past it. */
  function decidable(rule: PendingRule) {
    return !rule.scope_id || Boolean(radius[rule.rule_id])
  }

  /** Proposed by the person reading, which nobody approves. */
  function yours(rule: PendingRule) {
    return Boolean(me) && rule.proposed_by_user_id === me
  }

  function toggle(ruleId: string) {
    setPicked((was) => {
      const next = new Set(was)
      if (!next.delete(ruleId)) next.add(ruleId)
      return next
    })
  }

  const funds = useMemo(
    () => [...new Set(queue.map(appliesTo))].sort((a, b) => a.localeCompare(b)),
    [queue],
  )
  const layers = useMemo(
    () => [...new Set(queue.map((r) => r.layer))].sort((a, b) => a.localeCompare(b)),
    [queue],
  )

  const rows = useMemo(() => {
    const kept = queue.filter(
      (r) => (!fund || appliesTo(r) === fund) && (!layer || r.layer === layer) && (!desk || deskOf(r) === desk),
    )
    return sortRows(kept, sort, {
      desk: deskOf,
      fund: appliesTo,
      limit: (r) => r.rule_key,
      layer: (r) => layerLabel(r.layer),
    })
  }, [queue, fund, layer, sort, desk, desks])

  /* Bulk acts on what is on screen, never on a row a filter has hidden. Ticking
     twelve, narrowing to one fund and clicking Approve must not approve the
     eleven that are no longer visible — the tick means "this one", and a row
     nobody can see is a row nobody satisfied themselves about. */
  const selectable = rows.filter((r) => decidable(r) && !yours(r))
  const chosen = selectable.filter((r) => picked.has(r.rule_id))
  const everySelected = selectable.length > 0 && chosen.length === selectable.length
  const yoursCount = canApprove ? queue.filter(yours).length : 0

  async function decideMany(action: 'approve' | 'reject') {
    if (chosen.length === 0) return

    let reason = ''
    if (action === 'reject') {
      const asked = window.prompt(
        `Why are these ${chosen.length} being declined? The reason is recorded ` +
          'against each, and it is what the requester reads.',
      )
      if (!asked?.trim()) return
      reason = asked.trim()
    }

    setBusy('many')
    setError(null)
    setOutcome(null)
    setProgress({ done: 0, total: chosen.length })
    let done = 0
    const refused: Outcome['refused'] = []

    // One at a time and never aborting: a batch that stops at the first refusal
    // leaves the approver guessing which of the twenty went through.
    //
    // One at a time also means one round trip each, and a hundred and forty-six
    // of those is most of a minute. The first version showed nothing for the
    // whole of it — a disabled button, an unchanged table, and then the queue
    // emptying at once — which reads exactly like a screen that has hung. The
    // count is rendered as it goes, so the wait is visibly work.
    for (const rule of chosen) {
      try {
        if (action === 'approve') {
          const shown = rule.scope_id ? (radius[rule.rule_id]?.portfolio_ids ?? null) : null
          await api.approve(rule.rule_id, shown)
        } else {
          await api.rejectRule(rule.rule_id, reason)
        }
        done += 1
      } catch (e) {
        refused.push({ key: rule.rule_key, reason: e instanceof Error ? e.message : String(e) })
      }
      setProgress({ done: done + refused.length, total: chosen.length })
    }

    setPicked(new Set())
    setOutcome({ done, action, refused })
    setBusy(null)
    setProgress(null)
    await load()
  }

  async function decide(rule: PendingRule, action: 'approve' | 'reject') {
    setBusy(rule.rule_id)
    setError(null)
    try {
      if (action === 'approve') {
        const shown = rule.scope_id ? (radius[rule.rule_id]?.portfolio_ids ?? null) : null
        await api.approve(rule.rule_id, shown)
      } else {
        const reason = window.prompt(
          'Why is this being declined? The reason is recorded, and it is what ' +
            'the requester reads.',
        )
        if (!reason?.trim()) return
        await api.rejectRule(rule.rule_id, reason)
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      // Re-read the queue: a refusal usually means the store moved under us, and
      // the next attempt must be made against what is true now.
      await load()
    } finally {
      setBusy(null)
    }
  }

  const verb = outcome?.action === 'reject' ? 'declined' : 'approved'

  return (
    <div className="approvals">
      <header className="page-head">
        <h2>Proposals awaiting a decision</h2>
        <p className="note">
          Every proposed rule, wherever it came from — a migration, the mailbox,
          or somebody drafting here. This is the only place a limit is put into
          force.
        </p>
      </header>
      <Retirements canApprove={canApprove} me={me} />
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {outcome && (
        <div
          className={outcome.refused.length ? 'bulk-outcome warning' : 'bulk-outcome'}
          data-testid="bulk-outcome"
        >
          <p className={outcome.refused.length ? undefined : 'ok'}>
            {outcome.done} {verb}.
          </p>
          {byReason(outcome.refused).map(([reason, keys]) => (
            <div key={reason} className="refusal-group" data-testid="refusal-group">
              <p>
                <strong>
                  {keys.length} not {verb}.
                </strong>{' '}
                {sentence(reason)}
              </p>
              <ul className="hits">
                {keys.map((key) => (
                  <li key={key}>
                    <code>{key}</code>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {yoursCount > 0 && (
        <p className="note" data-testid="yours">
          {yoursCount === queue.length
            ? yoursCount === 1
              ? 'You proposed this one, so another approver puts it into force.'
              : `You proposed all ${yoursCount} of these, so another approver puts them into force.`
            : `${yoursCount} of these are yours, marked below, and another approver puts them into force.`}
        </p>
      )}

      {canApprove && rows.length > 0 && (
        /* Bulk is offered for the whole queue or not at all.
           *Approve selected* over a partial tick is the shape that invites
           somebody to work down the list ticking as they read and hit the
           button before they have finished reading — the buttons and the
           reading race each other. Ticking *everything* is a different act: it
           says "I have been through these", and it is a claim the approver
           makes deliberately rather than one they drift into. A partial
           selection still gets decided, one row at a time, from the row's own
           menu — which is where a decision about one rule belongs. */
        <div className="bulk-bar" data-testid="bulk-bar">
          <span>
            {progress
              ? `Working — ${progress.done} of ${progress.total} sent…`
              : chosen.length === 0
                ? 'Tick the ones you have satisfied yourself about.'
                : everySelected
                  ? `All ${chosen.length} selected.`
                  : `${chosen.length} of ${selectable.length} selected — tick them all to decide them together.`}
          </span>
          {progress && (
            <progress
              data-testid="bulk-progress"
              max={progress.total}
              value={progress.done}
              aria-label={`${progress.done} of ${progress.total} sent`}
            />
          )}
          {everySelected && !progress && (
            <>
              <button disabled={busy !== null} onClick={() => void decideMany('approve')}>
                Approve selected
              </button>
              <button
                className="danger"
                disabled={busy !== null}
                onClick={() => void decideMany('reject')}
              >
                Decline selected
              </button>
            </>
          )}
          {chosen.length > 0 && (
            <button className="link" onClick={() => setPicked(new Set())}>
              Clear
            </button>
          )}
          <Showing
            shown={rows.length}
            total={queue.length}
            noun="proposals"
            onClear={() => {
              setFund('')
              setLayer('')
              setDesk('')
            }}
          />
        </div>
      )}

      {!canApprove && (
        <Showing
          shown={rows.length}
          total={queue.length}
          noun="proposals"
          onClear={() => {
            setFund('')
            setLayer('')
            setDesk('')
          }}
        />
      )}

      {queue.length === 0 ? (
        <p className="empty">Nothing is waiting.</p>
      ) : rows.length === 0 ? (
        <p className="empty">No proposal matches those filters.</p>
      ) : (
        /* Widths are declared rather than left to the content. Left to it, the
           browser gave the longest cell on the table — *No document cited —
           approval will be refused until one is* — the narrowest column, because
           it sat beside a rule key that would not wrap. */
        <table className="grid proposals has-menus">
          <colgroup>
            {canApprove && <col className="col-tick" />}
            <col className="col-portfolio" />
            <col className="col-limit" />
            <col className="col-layer" />
            <col className="col-says" />
            <col className="col-source" />
            {canApprove && <col className="col-menu" />}
          </colgroup>
          <thead>
            <tr>
              {canApprove && (
                <th className="tick">
                  <input
                    type="checkbox"
                    aria-label="Select every proposal that can be decided"
                    checked={everySelected}
                    disabled={selectable.length === 0}
                    onChange={(e) =>
                      setPicked(
                        e.target.checked ? new Set(selectable.map((r) => r.rule_id)) : new Set(),
                      )
                    }
                  />
                </th>
              )}
              <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
              <FilterHeader
                label="Portfolio"
                sortKey="fund"
                sort={sort}
                onSort={onSort}
                value={fund}
                onChange={setFund}
                all="Every portfolio"
                testId="filter-portfolio"
                options={funds.map((f) => ({ value: f, label: f }))}
              />
              <SortHeader label="Limit" sortKey="limit" sort={sort} onSort={onSort} />
              <FilterHeader
                label="Set by"
                sortKey="layer"
                sort={sort}
                onSort={onSort}
                value={layer}
                onChange={setLayer}
                all="Every layer"
                testId="filter-layer"
                options={layers.map((l) => ({ value: l, label: layerLabel(l) }))}
              />
              <th>Says</th>
              <th>Comes from</th>
              {canApprove && <th>Decision</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((rule) => {
              const shown = radius[rule.rule_id]
              const waitingForRadius = Boolean(rule.scope_id) && !shown
              const said = says(rule)
              const own = yours(rule)

              return (
                <tr key={rule.rule_id} data-testid="proposal">
                  {canApprove && (
                    <td className="tick">
                      <input
                        type="checkbox"
                        aria-label={`Select ${rule.rule_key}`}
                        checked={picked.has(rule.rule_id)}
                        disabled={waitingForRadius || own}
                        title={own ? YOURS : undefined}
                        onChange={() => toggle(rule.rule_id)}
                      />
                    </td>
                  )}
                  <DeskCell fund={rule.portfolio_code} group={rule.scope_code} />
                  <td>
                    <span className="portfolio">{appliesTo(rule)}</span>
                    {/* §5.6, defect 3: what a scope rule reaches has to be on the
                        screen the decision is made on, not behind a click. In a
                        table that means the cell, not a panel underneath. */}
                    {rule.scope_id && (
                      <span className="radius" data-testid="radius">
                        <strong>
                          a group — reaches {shown?.portfolio_ids.length ?? '…'} fund(s)
                        </strong>
                        <span className="codes" title={shown?.portfolio_codes.join(', ')}>
                          {shown?.portfolio_codes.join(', ') ?? 'loading…'}
                        </span>
                      </span>
                    )}
                  </td>
                  <td>
                    <code>{rule.rule_key}</code>
                    {rule.measure && <span className="measure">{rule.measure}</span>}
                  </td>
                  <td>
                    <span className="pill pill-layer">{layerLabel(rule.layer)}</span>
                  </td>
                  <td>
                    <span className={said.numeric ? 'limit' : 'no-number'}>{said.text}</span>
                    {rule.supersedes_id &&
                    rule.prior_subject &&
                    JSON.stringify(rule.prior_subject) !== JSON.stringify(rule.subject) ? (
                      // The same limit, now covering different holdings.
                      // "amends an existing version" alone read as a copy of its
                      // predecessor, since the threshold is unchanged.
                      <span className="amends" data-testid="restates">
                        now covers {subjectText(rule.subject)}, where it covered{' '}
                        {subjectText(rule.prior_subject)}
                      </span>
                    ) : (
                      rule.supersedes_id && (
                        <span className="amends" data-testid="amends">
                          amends an existing version
                        </span>
                      )
                    )}
                  </td>
                  <td>
                    {rule.source_document_id ? (
                      <>
                        {/* `C15` is a cell of a spreadsheet, and a cell
                            reference names nothing unless the reader is told
                            which spreadsheet — so the most precise provenance in
                            the store was the least useful thing on the row. */}
                        {rule.source_document_kind === 'pack' ? (
                          <a
                            className="cited"
                            href={`/api/documents/${rule.source_document_id}/content`}
                            title="Opens the workbook this was read out of"
                          >
                            {rule.source_clause ? `cell ${rule.source_clause} of ` : 'cited to '}
                            {rule.source_document_title ?? 'a workbook'}
                          </a>
                        ) : (
                          <span className="cited">
                            {rule.source_clause ? `${rule.source_clause} of ` : 'cited to '}
                            {rule.source_document_title ?? 'a document'}
                          </span>
                        )}
                        {/* A pack is the client's own spreadsheet, not the
                            contract. It satisfies §5.5 — the rule can say where
                            it came from — and letting that read as settled
                            provenance is how a migration ends up agreeing with
                            itself. Named, so the approver knows which they have. */}
                        {rule.source_document_kind === 'pack' && (
                          <span className="flag" data-testid="pack-only">
                            ⚑ the client's own spreadsheet — no mandate document yet
                          </span>
                        )}
                      </>
                    ) : (
                      /* §5.5 and defect 6: approval will refuse this. Saying so
                         before the click is better than a 422 afterwards. */
                      <span className="warn" data-testid="no-provenance">
                        No document cited — approval will be refused until one is
                      </span>
                    )}
                    {/* Where it was read out of, which survives clause review
                        replacing the citation with the mandate document — and is
                        worth saying only once the two have diverged. While a
                        migrated rule still cites its own pack the citation above
                        *is* this, and the cell rendered the same file twice:
                        `cell C3 of sample_guidelines.csv` and then `open the
                        workbook (C3)` under it, two links to one document. */}
                    {rule.origin_document_id &&
                      rule.origin_document_id !== rule.source_document_id && (
                        <a
                          className="pack-link"
                          href={`/api/documents/${rule.origin_document_id}/content`}
                          title="Opens the workbook this was read out of"
                        >
                          read out of{' '}
                          {rule.source_anchor
                            ? `cell ${rule.source_anchor} of `
                            : ''}
                          {rule.origin_document_title ?? 'an imported workbook'}
                        </a>
                      )}
                    {rule.channel && <span className="channel">via {rule.channel}</span>}
                    {(own || rule.proposed_by) && (
                      <span className="channel" data-testid="proposed-by" title={own ? YOURS : undefined}>
                        proposed by {own ? 'you' : rule.proposed_by}
                      </span>
                    )}
                  </td>
                  {canApprove && (
                    <td className="actions">
                      <RowMenu
                        label={`Decide ${rule.rule_key}`}
                        testId="decision-menu"
                        items={[
                          {
                            label: 'Approve',
                            disabled: busy === rule.rule_id || waitingForRadius || own,
                            why: own
                              ? YOURS
                              : waitingForRadius
                                ? 'Not until this cell shows which funds the change reaches. Approving a group rule unseen is what this refusal exists to prevent.'
                                : undefined,
                            onSelect: () => void decide(rule, 'approve'),
                          },
                          {
                            label: 'Decline',
                            danger: true,
                            disabled: busy === rule.rule_id,
                            onSelect: () => void decide(rule, 'reject'),
                          },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {queue.length > 0 && !canApprove && (
        <p className="note">
          An approver decides these. You are seeing what is waiting, which is not
          the same as being able to put it into force.
        </p>
      )}
    </div>
  )
}
